import { Action, createReducer, on } from '@ngrx/store';

import { getQualityScore } from 'projects/labcorp/clinical-trials-ui/src/lib/utils';
import { ICandidate } from '../interfaces';
import { IListNavigator } from '@app/lib/interfaces';
import { IResearchStudyDisplay } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import * as CandidatesActions from './candidates.actions';
import * as utils from '@app/lib/utils';
import { CANDIDATES_FILTER_OPTIONS } from '../candidate-details.constants';

export interface ICandidatesState {
  candidates?: ICandidate[];
  candidateListNavigator?: IListNavigator;
  organizations?: fhir4.Organization[];
  organizationsProcessing?: boolean;
  researchStudies?: IResearchStudyDisplay[];
  researchStudiesProcessing?: boolean;
  selectedCandidate?: ICandidate;
  selectedOrganization?: fhir4.Organization;
  selectedResearchStudy?: IResearchStudyDisplay;
  viewArchivedCandidates: boolean;
}

export const initialState: ICandidatesState = {
  candidates: null,
  candidateListNavigator: {
    filterListOptions: CANDIDATES_FILTER_OPTIONS,
    filterParams: null
  },
  organizations: null,
  researchStudies: null,
  selectedCandidate: null,
  selectedOrganization: null,
  selectedResearchStudy: null,
  viewArchivedCandidates: false
};

// tslint:disable-next-line:typedef
export function candidatesReducer(existingState: ICandidatesState, action: Action) {
  return createReducer(
    initialState,
    on(CandidatesActions.getOrganizations, (state) => {
      return {
        ...state,
        organizationsProcessing: true
      };
    }),
    on(CandidatesActions.getOrganizationsSuccess, (state, { organizationsData }) => {
      return {
        ...state,
        organizations: organizationsData,
        organizationsProcessing: false
      };
    }),
    on(CandidatesActions.selectOrganization, (state, { organization }) => {
      return {
        ...state,
        selectedOrganization: organization,
        researchStudiesProcessing: true
      };
    }),
    on(CandidatesActions.getResearchStudiesSuccess, (state, { researchStudiesData }) => {
      return {
        ...state,
        researchStudies: researchStudiesData,
        researchStudiesProcessing: false
      };
    }),
    on(CandidatesActions.selectResearchStudy, (state, { researchStudy }) => {
      return {
        ...state,
        selectedResearchStudy: researchStudy
      };
    }),
    on(CandidatesActions.getCandidates, (state) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.candidateListNavigator);
      updatedListNavigator.processing = true;

      return {
        ...state,
        candidateListNavigator: updatedListNavigator
      };
    }),
    on(CandidatesActions.getCandidatesSuccess, (state, { candidatesData, paginationOptions, total }) => {
      const updatedCandidateListNavigator = utils.updateListNavigator(state.candidateListNavigator, paginationOptions, total);
      updatedCandidateListNavigator.filterListOptions = state.candidateListNavigator.filterListOptions;
      updatedCandidateListNavigator.filterParams = state.candidateListNavigator.filterParams;
      updatedCandidateListNavigator.processing = false;
      updatedCandidateListNavigator.reload = false;
      updatedCandidateListNavigator.resultsStartValue = (updatedCandidateListNavigator.pager.currentPageIndex * updatedCandidateListNavigator.pager.perPage) + 1;
      updatedCandidateListNavigator.resultsEndValue = updatedCandidateListNavigator.resultsStartValue + candidatesData.length - 1;

      return {
        ...state,
        candidates: candidatesData,
        candidateListNavigator: updatedCandidateListNavigator
      };
    }),
    on(CandidatesActions.setPageIndex, (state, { index }) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.candidateListNavigator);
      updatedListNavigator.pager.currentPageIndex = index;
      updatedListNavigator.pager.currentPage = index + 1;
      updatedListNavigator.processing = true;
      updatedListNavigator.reload = true;

      return {
        ...state,
        candidateListNavigator: updatedListNavigator
      };
    }),
    on(CandidatesActions.selectCandidate, (state, { candidate }) => {
      return {
        ...state,
        selectedCandidate: candidate
      };
    }),
    on(CandidatesActions.getCriteriaSuccess, (state, { group, questionnaireResponse }): ICandidatesState => {

      const updatedSelectedCandidate = utils.clone<ICandidate>(state.selectedCandidate);
      updatedSelectedCandidate.questionnaireResponse = questionnaireResponse;

      const updatedSelectedStudy = utils.clone<IResearchStudyDisplay>(state.selectedResearchStudy);
      updatedSelectedStudy.group = group;

      return {
        ...state,
        selectedCandidate: updatedSelectedCandidate,
        selectedResearchStudy: updatedSelectedStudy
      };
    }),
    on(CandidatesActions.clearAllSelected, (state) => {
      const currentOrganizations = state.organizations;

      return {
        ...initialState,
        organizations: currentOrganizations
      };
    }),
    on(CandidatesActions.clearSelectedCandidate, (state) => {
      return {
        ...state,
        selectedCandidate: null
      };
    }),
    on(CandidatesActions.updateCandidateDetailsSuccess, (state, { questionnaireResponse, researchSubject }): ICandidatesState => {

      const updatedSelectedCandidate = utils.clone<ICandidate>(state.selectedCandidate);
      updatedSelectedCandidate.candidateStatus = utils.getCandidateStatus(researchSubject.progress);
      updatedSelectedCandidate.dateAdded = utils.getDateAdded(researchSubject.progress);
      updatedSelectedCandidate.qualityScore = getQualityScore(researchSubject.extension);
      updatedSelectedCandidate.questionnaireResponse = questionnaireResponse;
      updatedSelectedCandidate.researchSubject = researchSubject;

      const updatedCandidates = [...state.candidates];
      const indexOfCandidate = updatedCandidates.findIndex(candidate => candidate.researchSubject.id === researchSubject.id);
      updatedCandidates[indexOfCandidate] = updatedSelectedCandidate;

      return {
        ...state,
        candidates: updatedCandidates,
        selectedCandidate: updatedSelectedCandidate
      };
    }),
    on(CandidatesActions.updateMultipleCandidateProgressSuccess, (state, { researchSubjects }): ICandidatesState => {

      const updatedCandidates = utils.clone<ICandidate[]>(state.candidates);
      updatedCandidates.forEach(candidate => {
        const researchSubject = researchSubjects.find(obj => candidate.researchSubject.id === obj.id);
        if (researchSubject) {
          candidate.candidateStatus = utils.getCandidateStatus(researchSubject.progress);
          candidate.dateAdded = utils.getDateAdded(researchSubject.progress);
          candidate.researchSubject = researchSubject;
        }
      });

      return {
        ...state,
        candidates: updatedCandidates
      };
    }),
    on(
      CandidatesActions.updateResearchSubjectStatusSuccess,
      CandidatesActions.updateMultipleResearchSubjectStatusSuccess, (state): ICandidatesState => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.candidateListNavigator);
      updatedListNavigator.reload = true;

      return {
        ...state,
        candidateListNavigator: updatedListNavigator
      };
    }),
    on(CandidatesActions.updateFilterParams, (state, { filterParam }) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.candidateListNavigator);

      if (filterParam.value) {
        if (updatedListNavigator.filterParams) {
          updatedListNavigator.filterParams[filterParam.paramName] = filterParam.value;
        }
        else {
          updatedListNavigator.filterParams = {
            [filterParam.paramName]: filterParam.value
          };
        }
      }
      else {
        delete updatedListNavigator.filterParams[filterParam.paramName];
      }

      return {
        ...state,
        candidateListNavigator: updatedListNavigator
      };
    }),
    on(CandidatesActions.updateFilterListOptions, (state, { filterListOptions }) => {
      const updatedListNavigator = utils.clone<IListNavigator>(state.candidateListNavigator);
      updatedListNavigator.filterListOptions = filterListOptions;

      return {
        ...state,
        candidateListNavigator: updatedListNavigator
      };
    }),
    on(CandidatesActions.updateEligibility, (state, { eligible }) => {
      const updatedSelectedCandidate = utils.clone<ICandidate>(state.selectedCandidate);
      updatedSelectedCandidate.eligible = eligible;

      return {
        ...state,
        selectedCandidate: updatedSelectedCandidate
      };
    }),
    on(CandidatesActions.toggleViewArchivedCandidates, (state) => {

      return {
        ...state,
        viewArchivedCandidates: !state.viewArchivedCandidates
      };
    }),
  )(existingState, action);
}

export const candidates = (state: ICandidatesState) => state.candidates;
export const candidateListNavigator = (state: ICandidatesState) => state.candidateListNavigator;
export const organizations = (state: ICandidatesState) => state.organizations;
export const organizationsProcessing = (state: ICandidatesState) => state.organizationsProcessing;
export const researchStudies = (state: ICandidatesState) => state.researchStudies;
export const researchStudiesProcessing = (state: ICandidatesState) => state.researchStudiesProcessing;
export const selectedCandidate = (state: ICandidatesState) => state.selectedCandidate;
export const selectedOrganization = (state: ICandidatesState) => state.selectedOrganization;
export const selectedResearchStudy = (state: ICandidatesState) => state.selectedResearchStudy;
export const viewArchivedCandidates = (state: ICandidatesState) => state.viewArchivedCandidates;
